import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

const TransactionPropertiesTab = ({ transaction }) => {
  const [copiedItems, setCopiedItems] = useState(new Set());

  const formatValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    const stringValue = String(value).trim();
    // Remove trailing zeros from decimal numbers
    if (/^\d+\.\d+$/.test(stringValue)) {
      return stringValue.replace(/\.0+$|(\.\d*[1-9])0+$/, '$1');
    }
    return stringValue.length > 0 ? stringValue : null;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItems(prev => new Set([...prev, label]));
      setTimeout(() => {
        setCopiedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(label);
          return newSet;
        });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const txHash = transaction.tx_hash || transaction.txHash || '';
  const functionName = transaction.function_name || transaction.functionName || 'N/A';

  const excludedFields = [
    'tx_hash', 'txHash', 'function_name', 'functionName',
    'id', 'ID',
    'contract_id', 'contractId', 'Contract Id',
    'team_id', 'teamId', 'Team Id'
  ];

  const excludedLabels = ['Id', 'Contract Id', 'Team Id'];

  const allProperties = [
    { label: 'Transaction Hash', value: formatValue(txHash), rawValue: txHash },
    { label: 'Function', value: formatValue(functionName), rawValue: functionName },
    ...Object.entries(transaction)
      .filter(([key]) => !excludedFields.includes(key))
      .map(([key, value]) => {
        const displayKey = key
          .replace(/_/g, ' ')
          .replace(/([A-Z])/g, ' $1')
          .trim()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        const formatted = formatValue(value);
        return {
          label: displayKey,
          value: formatted,
          rawValue: value
        };
      })
  ];

  const meaningfulProperties = allProperties.filter(prop =>
    prop.value !== null && !excludedLabels.includes(prop.label)
  );

  if (meaningfulProperties.length === 0) {
    return (
      <div className="px-6 py-8 text-center text-[#475569]">
        <p className="text-sm">No properties found.</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {meaningfulProperties.map((prop, index) => (
        <div
          key={`prop-${txHash}-${index}`}
          className="px-6 py-2 hover:bg-gray-50 transition-colors grid grid-cols-2 gap-4 items-center group"
        >
          <span
            className="text-xs font-medium text-[#0f172a] flex-shrink-0"
            style={{ fontFamily: 'Raleway, sans-serif' }}
          >
            {prop.label}
          </span>
          <div className="flex items-center space-x-2 min-w-0">
            <span
              className="text-sm text-[#475569] break-words flex-1 min-w-0"
              style={{ fontFamily: 'Raleway, sans-serif' }}
            >
              {prop.value}
            </span>
            <button
              onClick={() => {
                const valueToCopy = prop.rawValue !== undefined
                  ? (typeof prop.rawValue === 'object' ? JSON.stringify(prop.rawValue, null, 2) : String(prop.rawValue))
                  : prop.value;
                copyToClipboard(valueToCopy, prop.label);
              }}
              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 transition-all duration-200 flex-shrink-0"
              title="Copy to clipboard"
            >
              {copiedItems.has(prop.label) ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 text-[#475569]" />
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransactionPropertiesTab;
